// src/sim/hiding/on-floor.ts — P3 synthetic on-floor spot lifecycle (HIDE-07, D-23, D-25).
//
// Invariants:
//   - Pure: createFloorSpot returns a fresh object with a COPIED position
//     (never shares the reference with entity.position — mutating the entity
//     after the fact must not retroactively shift the synthetic spot).
//   - The `floor:${entity.id}` id convention is stable across replays and is
//     load-bearing for the HUD / perception layers that may filter floor spots
//     via prefix.
//   - applyDropToFloorIntent is a no-op unless status='free' AND the profile
//     has the 'on-floor' ability (HIDE-07 gate).
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-23, D-25.

import type { Entity } from '../../shared/types/world';
import type { HidingSpotAuth } from '../../shared/types/map';
import type { GameEvent } from '../../shared/types/events';
import type { CharacterProfile } from '../characters/character-profile';
import { P3_ON_FLOOR_SKILL_MOD } from './constants';

export interface DropToFloorResult {
  next: Entity;
  events: GameEvent[];
}

/**
 * Build a synthetic on-floor hiding spot for `entity`. The returned object is
 * fully-formed `HidingSpotAuth` and can be handed straight to `applyHideToggle`
 * or stored on `entity.floorSpot`. Position is a fresh copy.
 */
export function createFloorSpot(entity: Entity, profile: CharacterProfile): HidingSpotAuth {
  return {
    id: `floor:${entity.id}`,
    position: { x: entity.position.x, y: entity.position.y },
    size: profile.size,
    kind: 'on-floor',
    skillMod: P3_ON_FLOOR_SKILL_MOD,
    requiredAbilities: [],
    forbiddenCharacters: [],
    peekEnabled: true,
  };
}

/**
 * Consume a drop-to-floor intent. The entity must be free AND have the
 * 'on-floor' ability; otherwise the call is a no-op. On success, creates the
 * synthetic floorSpot, sets status='hiding', emits hiding-entered.
 */
export function applyDropToFloorIntent(
  entity: Entity,
  profile: CharacterProfile,
  currentTick: number,
): DropToFloorResult {
  if (entity.status !== 'free') return { next: entity, events: [] };
  if (!profile.abilities.includes('on-floor')) return { next: entity, events: [] };

  const spot = createFloorSpot(entity, profile);
  const next: Entity = {
    ...entity,
    status: 'hiding',
    hidingSpotId: null,
    floorSpot: spot,
  };
  return {
    next,
    events: [
      { type: 'hiding-entered', entityId: entity.id, spotId: spot.id, tick: currentTick },
    ],
  };
}
